import { Tools } from '@babylonjs/core/Misc/tools';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import '@babylonjs/core/Misc/screenshotTools';
import cloneDeep from 'lodash/cloneDeep';

import {
    ICameraGeometryPoint, IArcCameraAngles, SystemCamera, setFreeCameraFromGeometryPoint
} from '../System/systemCamera';
import { SceneryPipeline } from '../Scenery/sceneryPipeline';
import { EXPORT_FILTER_PREFIX } from './interface';

export interface ISize {
    width: number,
    height: number;
}

const frontView: IArcCameraAngles = {
    alpha: -Math.PI / 2,
    beta: Math.PI / 2,
    radius: 3
};

const backView: IArcCameraAngles = {
    alpha: Math.PI / 2,
    beta: Math.PI / 2,
    radius: 3
};

const leftView: IArcCameraAngles = {
    alpha: Math.PI,
    beta: Math.PI / 2,
    radius: 3
};

export const defaultViews: IArcCameraAngles[] | ICameraGeometryPoint[] = [
    frontView,
    backView,
    leftView,
];

//! Base64 Images string need to be divisible by 4 in Safari
// See https://stackoverflow.com/questions/27396376/base64-image-tag-in-safari-did-not-showed-up
const fixImageForSafari = (image: string): string => {
    const encodedImgString = 'data:image/jpeg;base64,';
    let imageBase64 = image.substring(encodedImgString.length - 1);
    while (imageBase64.length % 4 > 0) {
        imageBase64 += '=';
    }
    return encodedImgString + imageBase64;
};

export class ToolScreenshot {
    private system: SystemCamera;

    private sceneryPipeline: SceneryPipeline;

    private arcCamera: ArcRotateCamera;

    private freeCamera: FreeCamera;

    private defaultViews = cloneDeep(defaultViews);

    constructor(system: SystemCamera, sceneryPipeline: SceneryPipeline) {
        this.system = system;
        this.sceneryPipeline = sceneryPipeline;
        this.arcCamera = new ArcRotateCamera(`${EXPORT_FILTER_PREFIX}_screenshot_arcCamera`, -Math.PI / 2, Math.PI / 2, 10, new Vector3(0, 0, 0), system.scene);
        this.arcCamera.parent = system.parentCamera;
        this.arcCamera.minZ = 0;
        this.sceneryPipeline.addCamera(this.arcCamera);

        this.freeCamera = new FreeCamera(`${EXPORT_FILTER_PREFIX}_screenshot_freeCamera`, new Vector3(0, 0, 0), system.scene);
        this.freeCamera.parent = system.parentCamera;
        this.freeCamera.minZ = 0;
        this.sceneryPipeline.addCamera(this.freeCamera);
    }

    private height = 1000;

    private width = 1000;

    public setSize(size: ISize): void {
        this.width = size.width;
        this.height = size.height;
    }

    public getSize(): ISize {
        return { width: this.width, height: this.height };
    }

    public shot(callback: (image: string) => void): void {
        this.shotCameraGeometryPoint(null, callback);
    }

    public setDefaultViews(defaultViews: IArcCameraAngles[] | ICameraGeometryPoint[]): void {
        this.defaultViews = defaultViews;
    }

    public shotViews(
        views?: IArcCameraAngles[] | ICameraGeometryPoint[],
        callback?: (shots: string[]) => void
    ): void {
        if (!views) views = this.defaultViews;
        const l = views.length; const shots = [];
        let j = 0;
        for (let i = 0; i < l; i++) {
            const view = views[i];
            this.shotCamera(view, (image) => {
                shots.push(image);
                j++;
                if (j === l && callback) callback(shots);
            });
        }
    }

    public shotCamera(
        view: IArcCameraAngles | ICameraGeometryPoint,
        callback?: (image: string) => void
    ): void {
        if (view.radius) this.shotCameraArcAngles(view as IArcCameraAngles, callback);
        else this.shotCameraGeometryPoint(view as ICameraGeometryPoint, callback);
    }

    public shotCameraArcAngles(
        arcCameraAngles?: IArcCameraAngles,
        callback?: (image: string) => void
    ): void {
        if (arcCameraAngles) {
            this.arcCamera.alpha = arcCameraAngles.alpha;
            this.arcCamera.beta = arcCameraAngles.beta;
            this.arcCamera.radius = arcCameraAngles.radius;
        } else {
            this.arcCamera.alpha = this.system.arcCamera.alpha;
            this.arcCamera.beta = this.system.arcCamera.beta;
            this.arcCamera.radius = this.system.arcCamera.radius;
        }
        this.system.checkActiveMeshes();
        Tools.CreateScreenshotUsingRenderTarget(
            this.system.engine,
            this.arcCamera,
            { width: this.width, height: this.height },
            (image) => {
                image = fixImageForSafari(image);
                if (callback) callback(image);
            }, 'image/png', 4, null
        ); // AntiAliasing makes the image dark
    }

    public shotCameraGeometryPoint(
        geometryPoint?: ICameraGeometryPoint,
        callback?: (image: string) => void
    ): void {
        if (geometryPoint) {
            setFreeCameraFromGeometryPoint(this.freeCamera, geometryPoint);
        }
        this.system.checkActiveMeshes();
        Tools.CreateScreenshotUsingRenderTarget(
            this.system.engine,
            this.freeCamera,
            { width: this.width, height: this.height },
            (image) => {
                image = fixImageForSafari(image);
                if (callback) callback(image);
            }, 'image/png', 4, null
        ); // AntiAliasing makes the image dark
    }
}
